import { useState } from "react";
import SEO from "@/components/SEO";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Mail, Clock, MapPin, Send } from "lucide-react";
import { useNavigate } from "react-router-dom";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success("Message sent. We'll be in touch shortly.");
      nav("/thank-you");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to send");
    } finally {
      setLoading(false);
    }
  };

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <>
      <SEO title="Contact — Tech Alpha SSL" description="Contact Tech Alpha SSL — support, sales, and general inquiries." canonical="https://techalphassl.com/contact" />
      <section className="pt-20 pb-10">
        <div className="container-page max-w-4xl">
          <span className="eyebrow">Contact</span>
          <h1 className="mt-4 text-4xl md:text-6xl font-semibold tracking-tighter">Talk to a real engineer.</h1>
          <p className="mt-4 text-lg text-muted-foreground">We reply within one business hour, Monday–Friday.</p>
        </div>
      </section>

      <section className="pb-20">
        <div className="container-page grid gap-8 lg:grid-cols-3">
          <div className="lg:col-span-1 space-y-4">
            <div className="card-elevated p-6">
              <Mail className="h-5 w-5 text-[#1565FF] dark:text-[#4FC3F7]" />
              <h3 className="mt-3 text-sm font-semibold tracking-tight">Support Email</h3>
              <a href="mailto:techalphassl@gmail.com" className="mt-1 block text-sm text-muted-foreground hover:text-[#1565FF]" data-testid="contact-email-link">techalphassl@gmail.com</a>
            </div>
            <div className="card-elevated p-6">
              <Clock className="h-5 w-5 text-[#1565FF] dark:text-[#4FC3F7]" />
              <h3 className="mt-3 text-sm font-semibold tracking-tight">Business Hours</h3>
              <p className="mt-1 text-sm text-muted-foreground">Mon–Fri · 9:00 AM – 6:00 PM<br/>Sat · 10:00 AM – 2:00 PM<br/>Sun · Closed</p>
            </div>
            <div className="card-elevated p-6">
              <MapPin className="h-5 w-5 text-[#1565FF] dark:text-[#4FC3F7]" />
              <h3 className="mt-3 text-sm font-semibold tracking-tight">Global Coverage</h3>
              <p className="mt-1 text-sm text-muted-foreground">Serving businesses across 12+ countries. Remote-first support with regional response SLAs.</p>
              <div className="mt-4 aspect-video w-full rounded-lg bg-gradient-to-br from-[#1565FF]/10 to-[#4FC3F7]/10 border border-[#1565FF]/10 grid place-items-center text-xs text-muted-foreground">
                Map placeholder
              </div>
            </div>
          </div>

          <form onSubmit={submit} className="lg:col-span-2 card-elevated p-8 space-y-4" data-testid="contact-form">
            <div className="grid gap-4 md:grid-cols-2">
              <Field label="Name" required value={form.name} onChange={upd("name")} testId="contact-name" />
              <Field label="Email" type="email" required value={form.email} onChange={upd("email")} testId="contact-email" />
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <Field label="Phone (optional)" value={form.phone} onChange={upd("phone")} testId="contact-phone" />
              <Field label="Subject" value={form.subject} onChange={upd("subject")} testId="contact-subject" />
            </div>
            <div>
              <label className="text-sm font-medium">Message *</label>
              <textarea
                required
                rows={6}
                value={form.message}
                onChange={upd("message")}
                data-testid="contact-message"
                className="mt-1 w-full rounded-lg border border-black/10 dark:border-white/10 bg-white dark:bg-white/5 px-4 py-3 text-sm outline-none focus:border-[#1565FF]"
                placeholder="How can we help?"
              />
            </div>
            <button type="submit" disabled={loading} className="btn-primary" data-testid="contact-submit">
              {loading ? "Sending..." : "Send message"} <Send className="h-4 w-4" />
            </button>
          </form>
        </div>
      </section>
    </>
  );
}

function Field({ label, required, type = "text", value, onChange, testId }) {
  return (
    <div>
      <label className="text-sm font-medium">{label}{required && " *"}</label>
      <input
        required={required}
        type={type}
        value={value}
        onChange={onChange}
        data-testid={testId}
        className="mt-1 w-full rounded-lg border border-black/10 dark:border-white/10 bg-white dark:bg-white/5 px-4 py-3 text-sm outline-none focus:border-[#1565FF]"
      />
    </div>
  );
}
